import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, HelpCircle, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_URL } from '../utils/api';

const VERDICT_CONFIG = {
  'GO': {
    Icon: Check,
    bg: 'bg-qc-green',
    accent: 'text-qc-green',
    surfaceBg: 'bg-qc-green/10',
    borderColor: 'border-qc-green/30',
    label: 'GO',
    message: 'This product looks good!'
  },
  'MAYBE': {
    Icon: HelpCircle,
    bg: 'bg-qc-amber',
    accent: 'text-qc-amber',
    surfaceBg: 'bg-qc-amber/10',
    borderColor: 'border-qc-amber/30',
    label: 'MAYBE',
    message: 'Proceed with caution'
  },
  'HELL NO': {
    Icon: X,
    bg: 'bg-qc-red',
    accent: 'text-qc-red',
    surfaceBg: 'bg-qc-red/10',
    borderColor: 'border-qc-red/30',
    label: 'HELL NO',
    message: 'Best to skip this one'
  }
};

export default function PublicScan() {
  const { scanId } = useParams();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadScan();
  }, [scanId]);

  const loadScan = async () => {
    try {
      const response = await axios.get(`${API_URL}/scan/public/${scanId}`);
      setScan(response.data);
    } catch (err) {
      console.error('Failed to load scan:', err);
      setError(err.response?.status === 404 ? 'Scan not found' : 'Failed to load scan');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-qc-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-qc-green border-t-transparent" />
      </div>
    );
  }

  if (error || !scan) {
    return (
      <div className="min-h-screen bg-qc-bg flex flex-col items-center justify-center p-5" data-testid="public-scan-error">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-qc-surface rounded-full flex items-center justify-center mx-auto mb-4 border border-qc-border">
            <X className="w-8 h-8 text-qc-text-faint" />
          </div>
          <h1 className="text-2xl font-extrabold text-qc-text mb-2">
            {error || 'Scan not found'}
          </h1>
          <p className="text-qc-text-dim mb-6">
            This scan may have been removed or the link is invalid.
          </p>
          <Button asChild className="bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-full px-6 h-12 font-extrabold">
            <Link to="/signup" data-testid="public-scan-error-cta">Try Get Green Light</Link>
          </Button>
        </div>
      </div>
    );
  }

  const config = VERDICT_CONFIG[scan.decision] || VERDICT_CONFIG['MAYBE'];
  const Icon = config.Icon;

  return (
    <div 
      className="min-h-screen bg-qc-bg text-qc-text"
      data-testid="public-scan-page"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))' }}
    >
      <div className="max-w-lg mx-auto px-5 py-6">
        {/* Brand header */}
        <div className="text-center mb-6">
          <Link to="/login" className="text-2xl font-black text-qc-green tracking-tight">
            Get Green Light
          </Link>
          <p className="text-xs text-qc-text-faint mt-1">AI-powered food verdicts</p>
        </div>

        {/* Verdict card */}
        <div className={`rounded-3xl border ${config.borderColor} ${config.surfaceBg} p-6 mb-6`}>
          {/* Thumbnail + Verdict */}
          <div className="flex items-center gap-4 mb-5">
            {scan.thumbnail_url && (
              <div className="w-20 h-20 rounded-2xl overflow-hidden bg-qc-surface2 flex-shrink-0">
                <img src={scan.thumbnail_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg} mb-2`}>
                <Icon className="w-4 h-4 text-qc-bg" strokeWidth={3} />
                <span
                  className="text-sm font-black text-qc-bg tracking-wider"
                  data-testid="public-scan-decision"
                >
                  {config.label}
                </span>
              </div>
              <h1
                className="text-xl font-bold text-qc-text leading-tight"
                data-testid="public-scan-item-label"
              >
                {scan.item_label || 'Scanned Item'}
              </h1>
            </div>
          </div>

          {/* Reasons */}
          {scan.reasons && scan.reasons.length > 0 && (
            <div className="space-y-2 mb-5">
              <p className="text-xs font-bold text-qc-text-dim uppercase tracking-wider">Why?</p>
              <ul className="space-y-2" data-testid="public-scan-reasons">
                {scan.reasons.map((reason, idx) => (
                  <li key={`reason-${idx}-${reason.slice(0, 20)}`} className="flex items-start gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${config.bg} mt-2 flex-shrink-0`} />
                    <span className="text-sm text-qc-text-dim leading-relaxed">{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suggestion */}
          {scan.suggestion && (
            <div className="bg-qc-surface rounded-xl p-4 border border-qc-border">
              <p className="text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-1">
                What to look for instead
              </p>
              <p className="text-sm text-qc-text">{scan.suggestion}</p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="bg-qc-surface rounded-2xl border border-qc-border p-5 text-center">
          <h2 className="text-lg font-bold text-qc-text mb-2">
            Want personalized food verdicts?
          </h2>
          <p className="text-sm text-qc-text-dim mb-4">
            Get Green Light gives you instant GO / MAYBE / HELL NO verdicts based on your health goals.
          </p>
          <Button 
            asChild 
            className="w-full h-12 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-xl font-extrabold"
            data-testid="public-scan-cta"
          >
            <Link to="/signup">
              Start Free — 7 Scans on Us
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-qc-text-faint">
            Already have an account?{' '}
            <Link to="/login" className="text-qc-green font-medium">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
